import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowUp, Plus, MessageSquare, ChevronLeft, Sparkles, History, X,
  AlertTriangle, RotateCcw, ShoppingBag,
} from "lucide-react";
import { Header } from "../components/Header";
import { RecommendationCard, RichText } from "../components/RecommendationCard";
import { sendChat, streamChat, fetchRecentSearches, getSession } from "../lib/api";
import { recordSearch } from "../lib/userHistory";
import { CHAT } from "../constants/testIds";

const HISTORY_KEY = "sbi_history";

const loadHistory = () => {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]");
  } catch {
    return [];
  }
};

const saveHistory = (h) => localStorage.setItem(HISTORY_KEY, JSON.stringify(h.slice(0, 30)));

// Convert stored server-side messages [{role, content, search_id?}] to UI format.
const toUiMessages = (msgs = []) =>
  msgs.map((m) =>
    m.role === "user"
      ? { role: "user", content: m.content }
      : { role: "assistant", content: m.content, searchId: m.search_id }
  );

// Multi-phase AI thinking state. While we wait for chunks, the base phases
// rotate every ~2s. Once we've extracted the product name from the live
// stream (~2-4s in), the message switches to a confident "Writing the
// verdict for the <product>…" line so the user knows the AI has chosen.
const THINKING_PHASES = [
  { label: "Reading your question…",        emoji: "🔎" },
  { label: "Researching the UK market…",    emoji: "🇬🇧" },
  { label: "Comparing the top picks…",      emoji: "⚖️" },
  { label: "Weighing pros and cons…",       emoji: "🧠" },
  { label: "Writing your recommendation…",  emoji: "✍️" },
];

const PATIENCE_PHASES = [
  { label: "Still thinking — this one needs a closer look…", emoji: "🤔" },
  { label: "Almost there — finalising the verdict…",         emoji: "✨" },
];

const ThinkingState = ({ liveProductName }) => {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);
  // If we've identified the product from the stream, override the phase
  // with that — far more reassuring than a generic "thinking" line.
  let current;
  if (liveProductName) {
    current = { label: `Writing the verdict for the ${liveProductName}…`, emoji: "✍️" };
  } else if (elapsed < 10) {
    current = THINKING_PHASES[Math.min(THINKING_PHASES.length - 1, Math.floor(elapsed / 2))];
  } else if (elapsed < 20) {
    current = PATIENCE_PHASES[0];
  } else {
    current = PATIENCE_PHASES[1];
  }
  return (
    <div className="w-full animate-float-up" data-testid="chat-thinking">
      <div className="flex items-center gap-2.5 text-sm pl-1 mb-4">
        <div className="flex gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse-dot" />
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse-dot" style={{ animationDelay: "0.2s" }} />
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse-dot" style={{ animationDelay: "0.4s" }} />
        </div>
        <div key={current.label} className="flex items-center gap-2 text-muted-foreground animate-float-up">
          <span>{current.emoji}</span>
          <span data-testid="thinking-phase">{current.label}</span>
        </div>
      </div>
      <RecommendationSkeleton />
    </div>
  );
};

// Cheap, permissive extractor — finds ``"name":"..."`` inside the partial
// streamed JSON so we can show the product name the moment Claude commits
// to a choice (typically ~2-4s in).
const extractLiveProductName = (text) => {
  if (!text) return "";
  // Match the FIRST "name":"…" occurrence — by prompt structure that's
  // always best_recommendation.name (alternatives.name comes much later).
  const m = text.match(/"name"\s*:\s*"([^"\\]{2,80})"/);
  return m ? m[1] : "";
};

const RecommendationSkeleton = () => (
  <div
    data-testid="rec-skeleton"
    className="rounded-2xl border border-border bg-card overflow-hidden"
  >
    <div className="h-12 shimmer" />
    <div className="p-5 sm:p-6 space-y-5">
      <div className="h-16 rounded-xl shimmer" />
      <div className="flex flex-col sm:flex-row gap-5">
        <div className="w-full sm:w-48 h-40 sm:h-48 rounded-2xl shimmer shrink-0" />
        <div className="flex-1 space-y-3">
          <div className="h-7 w-3/4 rounded shimmer" />
          <div className="h-4 w-1/2 rounded shimmer" />
          <div className="h-9 w-32 rounded shimmer" />
          <div className="flex gap-2">
            <div className="h-9 w-28 rounded-lg shimmer" />
            <div className="h-9 w-28 rounded-lg shimmer" />
          </div>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="h-32 rounded-xl shimmer" />
        <div className="h-32 rounded-xl shimmer" />
      </div>
    </div>
  </div>
);

export default function Chat() {
  const navigate = useNavigate();
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState(loadHistory());
  const [recent, setRecent] = useState([]);
  const scrollRef = useRef(null);
  const initialDoneRef = useRef(false);

  useEffect(() => {
    fetchRecentSearches().then(setRecent).catch(() => {});
  }, []);

  useEffect(() => {
    if (initialDoneRef.current) return;
    const initial = location.state?.initial;
    if (initial) {
      initialDoneRef.current = true;
      send(initial);
    }
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  // Live product name extracted from the streamed JSON as soon as it appears
  // — feeds into ThinkingState so the user sees the chosen product within
  // a few seconds instead of waiting for the full card to land.
  const [liveProductName, setLiveProductName] = useState("");
  const streamBufferRef = useRef("");
  const abortStreamRef = useRef(null);
  // Monotonic id used to correlate a streamed ``parsed`` preview with its
  // subsequent ``done`` upgrade so the card updates in place instead of
  // duplicating.
  const streamIdRef = useRef(0);

  const send = (text) => {
    const message = (text ?? input).trim();
    if (!message || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: message }]);
    setLoading(true);
    setLiveProductName("");
    streamBufferRef.current = "";

    const onDone = (payload) => {
      setSessionId(payload.session_id);
      setMessages((m) => {
        // If a preview card was rendered from ``parsed``, upgrade it in
        // place so the user doesn't see the card flicker or duplicate.
        const idx = m.findIndex(
          (x) => x.role === "assistant" && x._streamingId === streamIdRef.current,
        );
        const final = { role: "assistant", content: payload.response, searchId: payload.search_id };
        if (idx >= 0) {
          const next = m.slice();
          next[idx] = final;
          return next;
        }
        return [...m, final];
      });
      const summary = payload.response?.query_summary || message;
      const newHist = [
        { id: payload.session_id, summary, ts: Date.now() },
        ...history.filter((h) => h.id !== payload.session_id),
      ];
      setHistory(newHist);
      saveHistory(newHist);
      // Local-only personalisation: record the search the user actually
      // ran so we can show it back on the homepage under "Based on your
      // searches". Stays in this browser — never sent to the server.
      recordSearch(summary, payload.response?.category || "");
      setLoading(false);
      setLiveProductName("");
      abortStreamRef.current = null;
    };

    const onError = () => {
      setMessages((m) => [
        ...m,
        // Marker the renderer uses to show a Retry button instead of a
        // dead-end "Sorry" message. The user's last input is captured so
        // a single tap re-sends it.
        { role: "assistant", error: { lastMessage: message } },
      ]);
      setLoading(false);
      setLiveProductName("");
      abortStreamRef.current = null;
    };

    const onChunk = (chunk) => {
      streamBufferRef.current += chunk;
      // Cheap regex — no perf concern at typical stream rates.
      if (!liveProductName) {
        const name = extractLiveProductName(streamBufferRef.current);
        if (name) setLiveProductName(name);
      }
    };

    // Preview event: LLM has finished + parsed, but Amazon enrichment
    // is still running. Render the card immediately with AI text —
    // Amazon fields (image, ASIN, live price) fill in on ``done``.
    const onParsed = (ai) => {
      streamIdRef.current += 1;
      const streamId = streamIdRef.current;
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: { ...ai, _preview: true },
          _streamingId: streamId,
        },
      ]);
      setLoading(false);
    };

    const onCached = () => {
      // No preview needed — ``done`` will arrive on the next tick.
    };

    abortStreamRef.current = streamChat(sessionId, message, {
      onChunk, onParsed, onDone, onCached, onError,
    });
  };

  const [scopedHistoryOpen, setScopedHistoryOpen] = useState(false);

  const newChat = () => {
    setMessages([]);
    setSessionId(null);
    setInput("");
    setScopedHistoryOpen(false);
  };

  const loadSession = async (sid) => {
    if (loading || sid === sessionId) return;
    setLoading(true);
    setMessages([]);
    try {
      const data = await getSession(sid);
      setSessionId(sid);
      setMessages(toUiMessages(data.messages || []));
    } catch (e) {
      // Session no longer exists on server — clean it from local history.
      const next = history.filter((h) => h.id !== sid);
      setHistory(next);
      saveHistory(next);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    send();
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <div className="flex-1 flex max-w-7xl w-full mx-auto">
        {/* Sidebar */}
        <aside className="hidden md:flex w-72 shrink-0 flex-col border-r border-border p-4 gap-4">
          <button
            onClick={newChat}
            data-testid={CHAT.newChatBtn}
            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm transition glow-emerald-soft"
          >
            <Plus className="w-4 h-4" strokeWidth={2.5} />
            New chat
          </button>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2 px-2">Your searches</div>
            <div className="space-y-0.5 max-h-[40vh] overflow-y-auto scroll-thin">
              {history.length === 0 && (
                <div className="text-xs text-muted-foreground px-2 py-3">No searches yet.</div>
              )}
              {history.map((h, i) => {
                const active = h.id === sessionId;
                return (
                  <button
                    key={h.id + i}
                    data-testid={CHAT.historyItem(i)}
                    onClick={() => loadSession(h.id)}
                    className={`w-full text-left text-sm px-3 py-2 rounded-lg transition truncate flex items-center gap-2 ${
                      active
                        ? "bg-emerald-500/10 border border-emerald-500/30 text-foreground"
                        : "hover:bg-secondary text-foreground/80 hover:text-foreground"
                    }`}
                    title={h.summary}
                  >
                    <MessageSquare className={`w-3.5 h-3.5 shrink-0 ${active ? "text-emerald-500" : "text-muted-foreground"}`} />
                    <span className="truncate">{h.summary}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2 px-2">Trending searches</div>
            <div className="space-y-0.5 max-h-[30vh] overflow-y-auto scroll-thin">
              {recent.slice(0, 8).map((s) => (
                <button
                  key={s.id}
                  onClick={() => send(s.query)}
                  className="w-full text-left text-xs px-3 py-2 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition truncate"
                  title={s.query}
                >
                  {s.query}
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Chat area */}
        <section className="flex-1 flex flex-col min-w-0 relative">
          <div className="md:hidden flex items-center gap-2 px-4 py-2 border-b border-border">
            <button
              onClick={() => navigate("/")}
              data-testid={CHAT.backHome}
              className="text-xs flex items-center gap-1 text-muted-foreground"
            >
              <ChevronLeft className="w-3.5 h-3.5" /> Home
            </button>
            <button
              onClick={() => setScopedHistoryOpen(true)}
              data-testid={CHAT.mobileHistoryBtn}
              className="ml-auto text-xs flex items-center gap-1 text-muted-foreground hover:text-foreground"
            >
              <History className="w-3.5 h-3.5" /> History ({history.length})
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto scroll-thin px-4 sm:px-8 py-6 pb-36">
            <div className="max-w-3xl mx-auto space-y-5">
              {messages.length === 0 && !loading && (
                <div className="flex flex-col items-center justify-center text-center py-16 sm:py-24">
                  <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mb-4">
                    <ShoppingBag className="w-6 h-6 text-emerald-500" strokeWidth={2} />
                  </div>
                  <h2 className="font-display font-bold text-2xl tracking-tight text-foreground mb-2">
                    Ask anything — get a clear buying verdict
                  </h2>
                  <p className="text-sm text-muted-foreground max-w-md">
                    Tell me what you&apos;re thinking of buying. I&apos;ll pick the best Amazon UK option, with pros, cons and an honest confidence score.
                  </p>
                </div>
              )}
              {messages.map((m, i) =>
                m.role === "user" ? (
                  <div key={i} className="flex justify-end animate-float-up">
                    <div className="max-w-[85%] sm:max-w-[80%] bg-emerald-500/10 text-foreground rounded-2xl rounded-tr-md px-4 py-3 text-sm sm:text-base border border-emerald-500/25">
                      {m.content}
                    </div>
                  </div>
                ) : m.error ? (
                  <div key={i} className="flex justify-start animate-float-up">
                    <div
                      data-testid="chat-error"
                      className="w-full bg-red-500/5 border border-red-500/30 rounded-2xl p-4 sm:p-5"
                    >
                      <div className="flex items-start gap-2.5 mb-3">
                        <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                        <p className="text-sm sm:text-base text-foreground leading-relaxed">
                          The AI service is briefly unavailable. We tried twice
                          automatically — tap retry in a moment to try again.
                        </p>
                      </div>
                      <button
                        onClick={() => send(m.error.lastMessage)}
                        disabled={loading}
                        data-testid="chat-error-retry"
                        className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg bg-emerald-500 text-slate-950 hover:bg-emerald-400 transition disabled:opacity-40"
                      >
                        <RotateCcw className="w-3.5 h-3.5" /> Retry
                      </button>
                    </div>
                  </div>
                ) : (
                  <div key={i} className="flex justify-start">
                    <div className="w-full">
                      {m.content?.intro && (
                        <div className="text-sm text-muted-foreground mb-3 flex items-start gap-2">
                          <Sparkles className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                          <div className="min-w-0">
                            <RichText text={m.content.intro} />
                          </div>
                        </div>
                      )}
                      <RecommendationCard data={m.content} searchId={m.searchId} />
                    </div>
                  </div>
                )
              )}
              {loading && <ThinkingState liveProductName={liveProductName} />}
            </div>
          </div>

          {/* Sticky input */}
          <div
            className="absolute bottom-0 inset-x-0 backdrop-blur-xl bg-background/80 border-t border-border px-4 sm:px-8 py-4"
            style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}
          >
            <form onSubmit={onSubmit} className="max-w-3xl mx-auto relative">
              <input
                data-testid={CHAT.input}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={loading}
                placeholder="Ask a follow-up or new buying question…"
                className="w-full bg-card border border-border rounded-2xl py-4 pl-5 pr-14 text-sm sm:text-base text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-emerald-500/60 focus:glow-emerald transition disabled:opacity-60"
              />
              <button
                type="submit"
                data-testid={CHAT.sendBtn}
                disabled={loading || !input.trim()}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 flex items-center justify-center transition disabled:opacity-40 disabled:cursor-not-allowed glow-emerald-soft active:scale-95"
                aria-label="Send"
              >
                <ArrowUp className="w-4 h-4" strokeWidth={2.5} />
              </button>
            </form>
          </div>
        </section>
      </div>

      {/* Mobile history drawer */}
      {scopedHistoryOpen && (
        <div className="fixed inset-0 z-50 md:hidden flex">
          <button
            className="flex-1 bg-black/70 backdrop-blur-sm"
            onClick={() => setScopedHistoryOpen(false)}
            aria-label="Close history"
          />
          <aside className="w-[85%] max-w-sm bg-card border-l border-border h-full overflow-y-auto scroll-thin">
            <div className="sticky top-0 bg-card border-b border-border px-4 py-3 flex items-center justify-between">
              <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">History</span>
              <button
                onClick={() => setScopedHistoryOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-secondary"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <button
                onClick={newChat}
                data-testid="mobile-new-chat-btn"
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm transition"
              >
                <Plus className="w-4 h-4" strokeWidth={2.5} />
                New chat
              </button>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mt-2">Your searches</div>
              {history.length === 0 && <div className="text-xs text-muted-foreground py-3">No searches yet.</div>}
              {history.map((h, i) => {
                const active = h.id === sessionId;
                return (
                  <button
                    key={h.id + i}
                    data-testid={`mobile-history-item-${i}`}
                    onClick={() => { loadSession(h.id); setScopedHistoryOpen(false); }}
                    className={`w-full text-left text-sm px-3 py-2.5 rounded-lg transition flex items-center gap-2 ${
                      active
                        ? "bg-emerald-500/10 border border-emerald-500/30 text-foreground"
                        : "hover:bg-secondary text-foreground/80 border border-transparent"
                    }`}
                  >
                    <MessageSquare className={`w-3.5 h-3.5 shrink-0 ${active ? "text-emerald-500" : "text-muted-foreground"}`} />
                    <span className="truncate">{h.summary}</span>
                  </button>
                );
              })}
              {recent.length > 0 && (
                <>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mt-4">Trending</div>
                  {recent.slice(0, 8).map((s) => (
                    <button
                      key={s.id}
                      onClick={() => { send(s.query); setScopedHistoryOpen(false); }}
                      className="w-full text-left text-xs px-3 py-2 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition truncate"
                    >
                      {s.query}
                    </button>
                  ))}
                </>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
